import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory, Redirect, useLocation } from "react-router-dom";
import loadable from "@loadable/component";
import Base64 from "crypto-js/enc-base64";
import Utf8 from "crypto-js/enc-utf8";
import Loading from "@kateinnovations/ui-library/Loading";
import { login } from "features/authentication";
import useAuth from "features/authentication/hooks/useAuth";
import { injectIntl } from "react-intl";
import Spinner from "@kateinnovations/ui-library/Loaders/Spinner";
import { ucFirst, getStorageItem, setStorageItem, apiCall } from "@kateinnovations/javascript-helpers";
import defaultConfig from "config/constants/defaultConfig";
import styles from "../resources/styles/login.module.scss";

import formData from "./constants/loginForm";

const Form = loadable(() => import(/* webpackChunkName: "LoginForm" */ "@kateinnovations/ui-library/Form"), {
    fallback: <Loading />,
});

const Notification = loadable(
    () => import(/* webpackChunkName: "Notification" */ "@kateinnovations/ui-library/Notification"),
    {
        fallback: <Loading />,
    }
);

const Login = ({ intl }) => {
    const history = useHistory();
    const location = useLocation();
    const isAuth = useAuth();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [showErrorMesage, setShowErrorMesage] = useState(false);
    const [loadRequest, setLoadRequest] = useState(false);
    const [disableForm, setDisableForm] = useState(false);
    const [formObject] = useState(() => formData(intl));
    const [kateReloaded] = useState(() => getStorageItem("kateReloaded") || false);
    const [kateReactVersion] = useState(() => getStorageItem("kateReactVersion") || undefined);
    const [redirectTo] = useState(() =>
        location?.state?.referer?.pathname
            ? `${location.state.referer.pathname}${location.state.referer?.search || ""}${
                  location.state.referer?.hash || ""
              }`
            : defaultConfig.path.react.dashboard
    );

    const customSubmitHandler = async (response) => {
        setLoadRequest(true);
        setDisableForm(true);
        if (!response.ok || "error" in response) {
            setErrorMessage("incorrectLoginDetails");
            setShowErrorMesage(true);
            setLoadRequest(false);
            setDisableForm(false);
            return false;
        }

        const { username, password } = response.data;
        const apiResponse = await login({ username, password });

        if (!apiResponse.ok) {
            setTimeout(async () => {
                const apiResponseError = await apiResponse.error();

                setLoadRequest(false);
                setDisableForm(false);
                setErrorMessage(apiResponseError.message);
                setShowErrorMesage(true);
            }, 750);

            return false;
        }

        const redirectUrl = history?.action === "REPLACE" ? redirectTo : defaultConfig.path.react.dashboard;
        const apiResponseData = await apiResponse.json();

        if (apiResponseData.twoFactor) {
            const locationState = JSON.stringify({
                redirectUrl,
                username,
                password,
                dateTime: new Date(),
                ...apiResponseData,
            });

            const wordArray = Utf8.parse(locationState);
            const base64 = Base64.stringify(wordArray);

            history.push(defaultConfig.path.react.auth.twoFactor, base64);
            return true;
        }

        setIsLoggedIn(true);
        return true;
    };

    const customEventHandler = (event) => {
        if (event.type === "focus" && !!errorMessage) setShowErrorMesage(false);
    };

    useEffect(() => {
        let ignore = false;
        const reloadPage = () => {
            setTimeout(() => {
                if (!ignore) {
                    setStorageItem("kateReloaded", true);
                    window.location.reload();
                }
            }, 350);
        };

        if (!kateReloaded) reloadPage();

        return () => {
            ignore = true;
            clearTimeout(reloadPage);
        };
    }, [kateReloaded]);

    useEffect(() => {
        let ignore = false;
        const reloadPage = ({ version }) => {
            setTimeout(() => {
                if (!ignore) {
                    setStorageItem("kateReactVersion", version);
                    window.location.reload();
                }
            }, 350);
        };
        const versionApiCall = async () => {
            const headers = {
                "Content-Type": "application/json",
            };
            const getResponse = await apiCall({
                url: defaultConfig.api.version.react,
                headers,
                method: "GET",
                credentials: "omit",
                mode: "cors",
            });

            const contentType = getResponse?.headers?.get("content-type")?.split(";")?.shift();

            if (getResponse.ok && contentType === "application/json") {
                const response = await getResponse.json();
                const version = response.data;

                if (version !== kateReactVersion) {
                    reloadPage({ version });
                }
            }
        };

        if (!ignore) versionApiCall();

        return () => {
            ignore = true;
            clearTimeout(reloadPage);
        };
    }, [kateReactVersion]);

    if (!kateReloaded) return null;

    if (isAuth || isLoggedIn) {
        if (redirectTo.startsWith("//")) {
            return (
                <Redirect
                    to={{ pathname: defaultConfig.path.react.auth.redirectToCore, state: { referer: redirectTo } }}
                />
            );
        }

        return <Redirect to={{ pathname: redirectTo || "/", state: undefined }} />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.unit} variant="info">
                <div className={styles.spinnerContainer}>
                    <Spinner state={loadRequest ? "visible" : "hidden"} />
                </div>
                <Notification state={showErrorMesage ? "visible" : "hidden"} variant="error">
                    <span>{errorMessage && ucFirst(intl.formatMessage({ id: `login.error.${errorMessage}` }))}</span>
                </Notification>
            </div>
            <article className={styles.unit} variant="form">
                <Form
                    {...formObject}
                    customSubmitHandler={customSubmitHandler}
                    customEventHandler={customEventHandler}
                    postFormWithApiCall={false}
                    disableForm={disableForm}
                />
            </article>
            <div>
                <a className={styles.button} href={defaultConfig.api.auth.resetPassword}>
                    {ucFirst(intl.formatMessage({ id: "user.changepassword.forgotPassword" }))}
                </a>
            </div>
        </div>
    );
};

Login.propTypes = {
    intl: PropTypes.shape({
        formatMessage: PropTypes.func,
    }).isRequired,
};

export default injectIntl(Login);
